import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../components/AuthContext';
import ActionButton from '../../components/ActionButton';
import { supabase } from '../../lib/supabase';
import { pageRuleAuth, pageRulePending } from '../../lib/deepLink';
import { canRecordWeldsAt, canClearWeldFlag, canSetWeldReserve, weldRefusalKind, weldCountRefusalKind } from '../../lib/permissions';
import { useTier } from '../../lib/tiers';
import { localDay } from '../../lib/transferSortFilter';
import {
  weldingBranchFor, loadWeldingBoard, flagsShown, startWeldFlag, stopWeldFlag, clearWeldFlag, undoWeldRecord, weldingDemand,
} from '../../lib/welding';
import {
  boardSubtitle, shelfLine, laterLine, fillPercent, flagWords, shortDay,
  NOTHING_TO_WELD, allCoveredLine, TAP_HINT, LOADING_LINE, COULD_NOT_LOAD,
  undoneAnswer, refusalWords, UNDOING, clearedAnswer, FLAG_ALREADY_ENDED, BRANCH_HINT, nothingWeldedAt,
  CUT_READ, binFirstLine, coatedNote, RECOUNT_LABEL, refusalReason, RESERVES_LABEL,
} from '../../lib/weldingWords';
import WeldingSizeSheet, { WeldingSizeDialog } from './WeldingSizeSheet';
import WeldingDesk from './WeldingDesk';
import { WeldingReservesSheet, WeldingReservesDialog } from './WeldingReserves';
import './welding.css';

// #746 - the Welding page (docs/gates/Stede_Welding_Gate_746.html), Session
// 79.22b.6.3. Houston's welders' board: how many of each frame and stake to
// weld, the earliest need still uncovered first (746 D33), and a way to record
// what they welded without touching To make (746 D1). BUILT EXACTLY AS GROK'S
// APPROVED FRAMES (746 D25): the phone board F1 and the tablet board F5, their
// scrolled frames keeping the header in place. Every number is weldingDemand()'s
// and every word is weldingWords.js's, from the gate's copy table.
//
// Who reaches it is the page rule in deepLink.js (746 D5); which branch it opens
// on is weldingBranchFor() (746 D23). While the sign-in has not yet heard which
// branches weld, the page shows itself loading rather than letting a rule that
// has not heard send the person elsewhere (pageRulePending()).
//
// Built at 79.22b.6.3: the board and its flags. Built at 79.22b.6.3.1: a
// size's sheet (WeldingSizeSheet.js, F2 and F4) opened by tapping its bin, and
// after a record the dark answer bar with Undo (F3). Built at 79.22b.6.4: the
// desk from 1024 wide (WeldingDesk.js, F6) with the size's window, Clear flag,
// and the admin's and executive's branch chip with S8. Built at 79.22b.6.4.1:
// the count sheet and the drawn states S1-S7. Built at 79.22b.6.4b: changing a
// reserve (WeldingReserves.js, R1-R4) - the Reserves button beside the total,
// and Change on a size's sheet, for those canSetWeldReserve() lets in.

// The tables the board listens to (746 D16): statement 29 of
// 20261010_welding_746.sql publishes the first three, 20261011_welding_reserve_746.sql
// the fourth.
export const LIVE_TABLES = ['weld_records', 'weld_counts', 'weld_flags', 'weld_reserves'];

// ONE SIZE'S BIN. A module-level component (locked rule 2), drawn as F1's
// `article.slot`: the name and its chip, still to weld against welded and
// waiting, the bar, the due line with the shelf and Later lines, and who is on
// it - your own flag carrying Stop, as F5 draws it.
//
// TAPPING IT OPENS THE SIZE'S SHEET (F3: "Always pressable; a viewer who cannot
// record still sees the size's orders and today's entries"), Session
// 79.22b.6.3.1. A press on the Stop inside it is Stop's own and opens nothing.
//
// S7 (79.22b.6.4.1): a size whose welded, waiting works out below zero shows 0,
// the note in #791's words, and Recount welded frames on the bin itself, which
// opens the size's sheet straight onto the count (S6). Someone who may not count
// still sees it, and a press answers with the reason (the F1 state table).
function WeldingBin({ size, horizonDays, flags, myId, onStop, stopping, onOpen, onCount, countReason }) {
  const covered = size.state === 'covered';
  const pct = fillPercent(size);
  const due = binFirstLine(size, horizonDays);
  const shelf = shelfLine(size.shelf);
  const later = laterLine(size.later);
  const who = flagWords(flags, myId);
  const open = e => { if (!e.target.closest('button')) onOpen(); };
  const key = e => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onOpen(); } };
  return (
    <article className="wp-slot" role="button" tabIndex={0} onClick={open} onKeyDown={key}>
      <div className="wp-nrow">
        <div className="wp-name">{size.name}</div>
        {size.lateChip && <span className="wp-chip late">Late</span>}
        {covered && <span className="wp-chip cov">Covered</span>}
      </div>
      <div className="wp-mid">
        <div className={`wp-big${covered ? ' done' : ''}`}>{size.stillToWeld}<span>Still to weld</span></div>
        <div className="wp-welded"><b>{size.waitingShown}</b>Welded, waiting</div>
      </div>
      <div className="wp-gauge" aria-hidden="true">{pct > 0 && <span style={{ width: `${pct}%` }} />}</div>
      <p className="wp-meta">
        {due}
        {shelf && <>{due && <br />}{shelf}</>}
        {later && <>{(due || shelf) && <br />}<span className="wp-later">{later}</span></>}
      </p>
      {size.belowZero && (
        <>
          <p className="wp-cnote">{coatedNote(size)}</p>
          <ActionButton className="wp-btn pri wp-binbtn" blockedReason={countReason} onClick={onCount}>{RECOUNT_LABEL}</ActionButton>
        </>
      )}
      {who && !who.mine && <p className="wp-flag">{who.text}</p>}
      {who && who.mine && (
        <div className="wp-own">
          <span>{who.text}</span>
          <ActionButton className="wp-btn stop" onClick={onStop} busy={stopping}>Stop</ActionButton>
        </div>
      )}
    </article>
  );
}

// THE BRANCH CHIP (746 D23; S8), admins and executives only: "Houston ▾ · Tap
// to switch branch". Stock Home's chip in shape (F5), drawn as S8's `.bchip`;
// the list is every branch, because an admin may look at one that welds
// nothing (S8). The choice lasts while the page is open (F3). Module-level
// (locked rule 2).
function BranchChip({ branches, branchId, coarse, onChoose }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const name = branches.find(b => String(b.id) === String(branchId))?.name || '';
  return (
    <div className="wp-bline" ref={ref}>
      <button type="button" className={`wp-bchip${coarse ? ' coarse' : ''}`} aria-expanded={open}
        aria-label={`${name}. ${BRANCH_HINT}`} onClick={() => setOpen(v => !v)}>
        {name}
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <span className="wp-bhint" aria-hidden="true">{BRANCH_HINT}</span>
      {open && (
        <ul className="wp-blist">
          {branches.map(b => (
            <li key={b.id}>
              <button type="button" aria-current={String(b.id) === String(branchId)}
                onClick={() => { setOpen(false); onChoose(b.id); }}>{b.name}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// R1 - THE RESERVES BUTTON (746 D31), beside the total on every width, its
// bottom on the total's (`.headtools`): 40 to a finger, 32 to a mouse on the
// desk. Drawn only for those allowed, and only once the board has loaded (F3).
// Module level (locked rule 2).
function ReservesButton({ onOpen }) {
  return <button type="button" className="wp-btn wp-btn40" onClick={onOpen}>{RESERVES_LABEL}</button>;
}

// THE STATES' HEADER (S1-S3, Session 79.22b.6.4.1): the board's own header -
// the title, the total or its skeleton when drawn, the admin's chip, and the
// line under it - on a phone and tablet as S1-S3 draw it, and on a desk as the
// desk's header (undrawn there; built from F6's parts). Module level (rule 2).
function StateHead({ desk, chip, sub, total }) {
  if (desk) {
    return (
      <div className="wp-dhead">
        <div>
          <h2>Welding</h2>
          {chip}
          {sub && <p className="wp-dsub">{sub}</p>}
        </div>
        {total && <div className="wp-dtools">{total}</div>}
      </div>
    );
  }
  return (
    <div className="wp-head">
      <div className="wp-top">
        <h2>Welding</h2>
        {total}
      </div>
      {chip}
      {sub && <p className="wp-sub">{sub}</p>}
    </div>
  );
}

// S1's skeleton: the total's two bars, and three bins of grey bars, the widths
// Grok drew.
const SKELETON_TOTAL = (
  <div className="wp-total" aria-hidden="true"><div className="wp-sk wp-sk-total" /><div className="wp-sk wp-sk-cap" /></div>
);
const SKELETON_BINS = [
  { name: 72, bar: 46, meta: 84 },
  { name: 58, bar: 28, meta: 70 },
  { name: 64, bar: 62, meta: 48 },
];
function SkeletonBins() {
  return (
    <div className="wp-list" aria-hidden="true">
      {SKELETON_BINS.map((b, i) => (
        <article key={i} className="wp-slot">
          <div className="wp-sk wp-sk-name" style={{ width: `${b.name}%` }} />
          <div className="wp-mid"><div className="wp-sk wp-sk-num" /><div className="wp-sk wp-sk-wait" /></div>
          <div className="wp-gauge sk"><span style={{ width: `${b.bar}%` }} /></div>
          <div className="wp-sk wp-sk-meta" style={{ width: `${b.meta}%` }} />
        </article>
      ))}
    </div>
  );
}

export default function WeldingPage() {
  const auth = useAuth();
  const { tier, coarse } = useTier();
  // F5's sizes on a tablet; the desk's own layout (F6) from 1024 wide, its
  // controls a mouse's unless the pointer is a finger (#417).
  const desk = tier === 'desk' || tier === 'wide';
  const shell = desk
    ? `wp wp-desk${tier === 'wide' ? ' wide' : ''}${coarse ? ' coarse' : ''}`
    : `wp${tier === 'phone' ? '' : ' wp-tab'}`;
  const pending = pageRulePending(pageRuleAuth(auth), 'welding');
  // 746 D23: the page opens on weldingBranchFor(); an admin or executive may
  // switch with the chip, and the choice lasts while the page is open.
  const mayChoose = !!(auth.isAdmin || auth.isExecutive);
  const [chosenBranchId, setChosenBranchId] = useState(null);
  const branchId = pending ? null
    : (mayChoose && chosenBranchId != null ? chosenBranchId : weldingBranchFor(auth));
  const weldingIds = Array.isArray(auth.weldingBranchIds) ? auth.weldingBranchIds.map(String) : [];
  const weldsHere = branchId != null && weldingIds.includes(String(branchId));
  const profileBranchId = auth.profile?.branch_id;
  const profileBranchName = auth.profile?.branch_name;

  const [board, setBoard] = useState(null);       // null while loading
  const [branchName, setBranchName] = useState('');
  const [stopping, setStopping] = useState(false);
  // Every branch, for the chip and for S8's sentence - read only for those
  // who may choose.
  const [branches, setBranches] = useState(null);
  // Which desk press is busy: `help-<item>` or `clear-<flag>`.
  const [busyKey, setBusyKey] = useState(null);
  const [stopError, setStopError] = useState('');
  // The size whose sheet is open, by item id (F2-F4), and the answer bar (F3):
  // { text, recordId } - recordId only while Undo is offered.
  const [openItemId, setOpenItemId] = useState(null);
  // S7's bin button opens the size's sheet straight onto the count (S6).
  const [countFirst, setCountFirst] = useState(false);
  // R3, R4: the every-size reserve list is open.
  const [reservesOpen, setReservesOpen] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const answerRef = useRef(null);
  const flagMapRef = useRef(new Map());
  const myIdRef = useRef(null);

  // The board's six reads (B3) and the numbers worked out for today's date. A
  // read that failed or came back cut is the error state, never a short list.
  const load = useCallback(async () => {
    // A branch that welds nothing has no board to read (S8).
    if (branchId == null || !weldsHere) return;
    // DEVELOPMENT ONLY - THE MEASURING SWITCH (746 D25, X1 line 22; Session
    // 79.22b.6.3, piece 7). To hold the built board to Grok's frame element by
    // element, the page must show the frame's own numbers at the frame's own
    // date and clock. The measuring script (docs/audits/79.22b.6.3/
    // measure_board_746.js) hands them over here, and they still go through the
    // real weldingDemand(), so nothing is written to any database and nothing is
    // invented. Create React App replaces process.env.NODE_ENV when it builds,
    // so in the version anyone uses this condition is the constant false and
    // the branch is removed; weldingBoard746.test.js holds every mention of the
    // switch to this guard.
    if (process.env.NODE_ENV === 'development' && window.__weldingFixture) {
      const f = window.__weldingFixture;
      // 79.22b.6.4.1 adds the states' frames: S1 holds the page loading, and S2
      // is a failed or a cut read (`failed`: 'read' or 'cut').
      if (f.loading) return;
      if (f.failed) { setBoard({ ok: false, reason: f.failed, today: f.today }); return; }
      // 79.22b.6.3.1 adds the sheet's frames (F2, F3, F4): who is looking
      // (myId, and the two refusal kinds - null for someone who may), today's
      // entries for the open size, and F3's answer bar. All optional.
      setBoard({
        ok: true, flags: f.flags, horizonDays: f.horizonDays, coatShipDays: f.coatShipDays,
        today: f.today, dayStart: new Date(f.dayStart), mayRecord: f.mayRecord, branchName: f.branchName,
        demand: weldingDemand({ ...f.rows, branchId, horizonDays: f.horizonDays, coatShipDays: f.coatShipDays, today: f.today }),
        ...(f.sheet ? { sheet: f.sheet } : {}),
      });
      if (f.sheet?.answer) setAnswer(f.sheet.answer);
      return;
    }
    const today = localDay();
    const result = await loadWeldingBoard(branchId, { today });
    setBoard({ ...result, today });
  }, [branchId, weldsHere]);

  useEffect(() => {
    setBoard(null);
    load();
  }, [load]);

  // LIVE (746 D16, piece 5): a record, an Undo, a count, a flag or a reserve at
  // this branch, by anybody, re-reads the board within seconds - the bell's own
  // pattern (Topbar.js: one channel, removed in the cleanup). The four tables
  // are the ones the two welding migrations publish; each subscriber still sees
  // only the rows its read rule allows. Changes arriving together re-read once.
  // A sign-off on To make is not here, by design: welded, waiting falls "on the
  // next view" (gate X1).
  useEffect(() => {
    if (branchId == null || !weldsHere) return undefined;
    let timer = null;
    const reread = () => {
      clearTimeout(timer);
      timer = setTimeout(load, 300);
    };
    let channel = supabase.channel(`welding-board-${branchId}`);
    for (const table of LIVE_TABLES) {
      channel = channel.on('postgres_changes',
        { event: '*', schema: 'public', table, filter: `branch_id=eq.${branchId}` }, reread);
    }
    channel.subscribe();
    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [branchId, weldsHere, load]);

  useEffect(() => {
    if (!mayChoose) return undefined;
    let cancelled = false;
    supabase.from('branches').select('id, name').order('id')
      .then(({ data, error }) => { if (!cancelled && !error) setBranches(data || []); });
    return () => { cancelled = true; };
  }, [mayChoose]);

  // The branch's name for the line under the title: the person's own from the
  // profile, read only when the page shows another branch (an admin's, D23).
  useEffect(() => {
    if (branchId == null) return undefined;
    if (String(profileBranchId) === String(branchId) && profileBranchName) {
      setBranchName(profileBranchName);
      return undefined;
    }
    let cancelled = false;
    supabase.from('branches').select('name').eq('id', branchId).maybeSingle()
      .then(({ data }) => { if (!cancelled) setBranchName(data?.name || ''); });
    return () => { cancelled = true; };
  }, [branchId, profileBranchId, profileBranchName]);

  // THE ANSWER BAR LASTS AS LONG AS ITS ANSWER (F3, CardNote's rule; 746 D13
  // as corrected - no clock): until the next press anywhere on the page, a
  // change of branch, or leaving the page. A press on the bar itself is its own.
  const shownBranch = useRef(branchId);
  useEffect(() => {
    const was = shownBranch.current;
    shownBranch.current = branchId;
    // Only a CHANGE of branch (the admin's chip, 79.22b.6.4) - not the page
    // first learning which branch it is on.
    if (was == null || branchId == null || String(was) === String(branchId)) return;
    setAnswer(null);
    setOpenItemId(null);
    setReservesOpen(false);
  }, [branchId]);
  useEffect(() => {
    if (!answer) return undefined;
    const clear = e => { if (!answerRef.current?.contains(e.target)) setAnswer(null); };
    document.addEventListener('pointerdown', clear, true);
    return () => document.removeEventListener('pointerdown', clear, true);
  }, [answer]);

  // Clear flag (746 D15; 79.22b.6.4 R1 D2, one per person): the answer names
  // whose flag it was; a flag that had already ended changes nothing, and says so.
  // From a desk row the answer is the bar (C5); from inside an open sheet or
  // window it is returned to be shown there, since the bar sits under the
  // sheet's backdrop (Grok's comparison, finding 3).
  async function clear(f, { inSheet = false } = {}) {
    setBusyKey(`clear-${f.id}`);
    const r = await clearWeldFlag(f.id);
    setBusyKey(null);
    const text = !r.ok ? refusalWords(r.error) : r.cleared ? clearedAnswer(f.user_name) : FLAG_ALREADY_ENDED;
    if (!inSheet) setAnswer({ text });
    load();
    return text;
  }

  // Help on this, from a desk row. When the press would be refused, or would
  // move you off another size, the size's window opens instead, where the
  // reason or the "This moves you" sentence stands before the press (F3) -
  // a desk row has no room to print either.
  async function help(size) {
    const elsewhere = [...flagMapRef.current].some(([key, list]) => key !== String(size.itemId)
      && list.some(f => myIdRef.current != null && String(f.user_id) === String(myIdRef.current)));
    if (weldRefusalKind(auth, branchId) || elsewhere) { setCountFirst(false); setOpenItemId(size.itemId); return; }
    setBusyKey(`help-${size.itemId}`);
    const r = await startWeldFlag({ branchId, itemId: size.itemId });
    setBusyKey(null);
    if (!r.ok) setAnswer({ text: refusalWords(r.error) });
    load();
  }

  async function stop() {
    setStopping(true);
    setStopError('');
    const r = await stopWeldFlag();
    setStopping(false);
    if (r.error) { setStopError(refusalWords(r.error)); return; }
    load();
  }

  // Undo, on the answer (746 D13, D20): the answer becomes "Undone. Welded,
  // waiting is back to 10."; a refusal replaces it with its reason.
  async function undo() {
    if (!answer?.recordId) return;
    setUndoing(true);
    const r = await undoWeldRecord(answer.recordId);
    setUndoing(false);
    setAnswer(r.ok ? { text: undoneAnswer(r.weldedWaiting) } : { text: refusalWords(r.error) });
    load();
  }

  // Nobody welds anywhere yet: the sign-in has heard, and the list is empty.
  if (!pending && branchId == null && Array.isArray(auth.weldingBranchIds) && !auth.weldingBranchIds.length) {
    return <div className={shell}><p className="wp-wait">No branch welds anything yet.</p></div>;
  }

  // The header's second line: the branch chip for an admin or executive (746
  // D23, S8), then the date and order without the branch name the chip
  // already says; for anyone else, the copy table's line as drawn (F1, F6).
  const chip = mayChoose && branches && branchId != null
    ? <BranchChip branches={branches} branchId={branchId} coarse={coarse} onChoose={setChosenBranchId} />
    : null;

  // S8 - AN ADMIN OR EXECUTIVE LOOKING AT A BRANCH THAT WELDS NOTHING:
  // "Nothing is welded at Dallas, because Houston welds the frames and stakes."
  if (!pending && branchId != null && !weldsHere && branches) {
    const here = branches.find(b => String(b.id) === String(branchId))?.name || '';
    const weldingNames = branches.filter(b => weldingIds.includes(String(b.id))).map(b => b.name);
    return (
      <div className={`${shell}${desk ? ' none' : ''}`}>
        <div className={desk ? 'wp-dhead' : 'wp-head'}>
          <div><h2>Welding</h2>{chip}</div>
        </div>
        {desk ? <p className="wp-say">{nothingWeldedAt(here, weldingNames)}</p>
          : <div className="wp-list"><p className="wp-say">{nothingWeldedAt(here, weldingNames)}</p></div>}
      </div>
    );
  }

  // S1 - LOADING: the header with the total's skeleton, "Loading the welding
  // list." under the title, and skeleton bins (the desk keeps its header).
  if (branchId == null || board == null || !weldsHere) {
    return (
      <div className={shell} aria-busy="true">
        <StateHead desk={desk} chip={chip} sub={LOADING_LINE} total={SKELETON_TOTAL} />
        <SkeletonBins />
      </div>
    );
  }

  // The line under the title in S2 and S3: the branch and the day, without the
  // board's order ("weld-by dates, late first"), since there is no list to order.
  const stateSub = [chip ? '' : branchName, shortDay(board.today || localDay())].filter(Boolean).join(' · ');

  // S2 - COULD NOT LOAD, with Try again; a read cut at 1,000 rows says why
  // instead, and offers no Try again, which would bring back the same cut list.
  if (!board.ok) {
    const cut = board.reason === 'cut';
    return (
      <div className={shell}>
        <StateHead desk={desk} chip={chip} sub={stateSub} />
        <div className={`wp-state${desk ? ' desk' : ''}`} role="alert">
          <p className="wp-fail">{cut ? CUT_READ : COULD_NOT_LOAD}</p>
          {!cut && <button type="button" className="wp-btn pri" onClick={() => { setBoard(null); load(); }}>Try again</button>}
        </div>
      </div>
    );
  }

  const { demand, flags, horizonDays, coatShipDays, today } = board;
  // `dayStart`, `mayRecord`, `mayReserve` and `branchName` on the board come
  // only from the measuring switch above; a real load leaves them unset.
  const shownBranchName = board.branchName ?? branchName;

  // R1-R4 (746 D29): who may change a reserve here - a manager at this branch,
  // an admin or an executive. The database's weld_reserve_ok() decides the same.
  const mayReserve = board.mayReserve ?? canSetWeldReserve(auth, branchId);
  const reservesButton = mayReserve && <ReservesButton onOpen={() => { setOpenItemId(null); setReservesOpen(true); }} />;
  // The every-size list reads the board's own sizes, every one of them - a size
  // with nothing on order can still be given a reserve - so it opens at once.
  const reservesProps = {
    sizes: demand.sizes,
    branchId,
    branchName: shownBranchName,
    onClose: () => setReservesOpen(false),
    onSaved: a => { setReservesOpen(false); setAnswer(a); load(); },
    onChanged: load,
  };
  const reservesList = mayReserve && reservesOpen
    && (desk ? <WeldingReservesDialog {...reservesProps} /> : <WeldingReservesSheet {...reservesProps} />);

  // S3 - NOTHING TO WELD: no order, no low shelf, no reserve, nothing Later and
  // nothing welded, waiting (the gate's F1 state table, as amended by 746 D33).
  // The board has loaded, so Reserves stands: a reserve is how a size with
  // nothing on order gets something to weld.
  if (demand.nothingToWeld) {
    return (
      <div className={shell}>
        <StateHead desk={desk} chip={chip} sub={stateSub}
          total={reservesButton ? <div className="wp-htools">{reservesButton}</div> : undefined} />
        <div className={`wp-state${desk ? ' desk' : ''}`}><p>{NOTHING_TO_WELD}</p></div>
        {answer && (
          <div className="wp-undo" ref={answerRef} role="status"><p>{answer.text}</p></div>
        )}
        {reservesList}
      </div>
    );
  }

  // The board shows what is still to weld, what is Covered, and the two undrawn
  // edges - only Later orders, and welded frames with nothing on order
  // (79.22b.6.4.1); a size with none of those is for the every-size reserve list.
  const shown = demand.sizes.filter(s => s.onBoard);
  const flagMap = flagsShown(flags, { sizes: demand.sizes, ...(board.dayStart ? { dayStart: board.dayStart } : {}) });
  flagMapRef.current = flagMap;
  // `board.sheet` too is the measuring switch's alone: who is looking, and
  // today's entries, for the sheet's frames.
  const fixture = board.sheet || null;
  const myId = fixture && 'myId' in fixture ? fixture.myId : auth.profile?.id;
  myIdRef.current = myId;
  const mayClear = canClearWeldFlag(auth, branchId);
  // The tap hint is only for someone who may record here (the copy table).
  const mayRecord = board.mayRecord ?? canRecordWeldsAt(auth, branchId);
  const anyReserve = demand.sizes.some(s => s.reserve > 0);

  // The open size's sheet. It reads the board's own size, so a record, an Undo
  // or a flag arriving live redraws it in place.
  const openSize = openItemId == null ? null
    : demand.sizes.find(s => String(s.itemId) === String(openItemId)) || null;
  // "You're on Frame Std 24x18 Top Rider" - your open flag on another size today.
  let myFlagElsewhere = null;
  if (openSize && myId != null) {
    for (const [itemKey, list] of flagMap) {
      if (itemKey === String(openSize.itemId)) continue;
      if (list.some(f => String(f.user_id) === String(myId))) {
        myFlagElsewhere = demand.sizes.find(s => String(s.itemId) === itemKey)?.name || null;
        break;
      }
    }
  }

  const subLine = boardSubtitle({ branchName: chip ? '' : shownBranchName, today, coatShipDays });
  const notes = (
    <>
      {stopError && <p className="wp-note" role="alert">{stopError}</p>}
      {/* S4: the sentence above the list, the total reading 0. */}
      {demand.everythingCovered && <p className="wp-coverline">{allCoveredLine(horizonDays, anyReserve)}</p>}
    </>
  );
  const answerBar = answer && (
    <div className="wp-undo" ref={answerRef} role="status">
      <p>{answer.text}</p>
      {answer.recordId != null && (
        <ActionButton className="wp-btn pri" busy={undoing} busyLabel={UNDOING} onClick={undo}>Undo</ActionButton>
      )}
    </div>
  );
  // Who may count here, for S7's button on a bin or a desk row (the sheet asks
  // the same, below).
  const countKind = fixture && 'countRefusalKind' in fixture ? fixture.countRefusalKind : weldCountRefusalKind(auth, branchId);
  const binCountReason = refusalReason(countKind, shownBranchName);
  const sheetProps = openSize && {
    size: openSize,
    branchId,
    branchName: shownBranchName,
    horizonDays,
    coatShipDays,
    sizeFlags: flagMap.get(String(openSize.itemId)) || [],
    myId,
    myFlagElsewhere,
    refusalKind: fixture && 'refusalKind' in fixture ? fixture.refusalKind : weldRefusalKind(auth, branchId),
    countRefusalKind: countKind,
    startCount: countFirst,
    todayOverride: fixture?.today?.[String(openSize.itemId)],
    // Clear flag in the sheet and the desk's window (C1, C2, C4): the sheet
    // stays open and the cleared person's line goes.
    mayClear,
    clearingId: busyKey && busyKey.startsWith('clear-') ? busyKey.slice(6) : null,
    onClear: f => clear(f, { inSheet: true }),
    onClose: () => setOpenItemId(null),
    onRecorded: a => { setOpenItemId(null); setAnswer(a); load(); },
    onChanged: load,
    // Change on the reserve line (R2), for those allowed; `startReserve` opens
    // the sheet straight onto it - the measuring switch's alone, for R2's frame.
    mayReserve,
    startReserve: !!fixture?.startReserve,
  };

  // THE DESK (F6): the header with the total, the next-up card, the summary
  // line and the table; Record opens the size's window.
  if (desk) {
    return (
      <div className={shell}>
        <div className="wp-dhead">
          <div>
            <h2>Welding</h2>
            {chip}
            <p className="wp-dsub">{subLine}</p>
          </div>
          <div className="wp-dtools">
            {reservesButton}
            <div className="wp-total"><b>{demand.stillToWeld}</b><span>Still to weld</span></div>
          </div>
        </div>
        <WeldingDesk
          sizes={shown}
          horizonDays={horizonDays}
          flagMap={flagMap}
          myId={myId}
          mayClear={mayClear}
          busyKey={stopping ? 'stop' : busyKey}
          notes={notes}
          onRecord={size => { setCountFirst(false); setOpenItemId(size.itemId); }}
          onCount={size => { setCountFirst(true); setOpenItemId(size.itemId); }}
          countReason={binCountReason}
          onHelp={help}
          onClear={clear}
          onStop={stop}
        />
        {answerBar}
        {openSize && <WeldingSizeDialog {...sheetProps} />}
        {reservesList}
      </div>
    );
  }

  return (
    <div className={shell}>
      <div className="wp-head">
        <div className="wp-top">
          <h2>Welding</h2>
          <div className="wp-htools">
            {reservesButton}
            <div className="wp-total"><b>{demand.stillToWeld}</b><span>Still to weld</span></div>
          </div>
        </div>
        {chip}
        <p className="wp-sub">{subLine}</p>
        {mayRecord && <p className="wp-hint">{TAP_HINT}</p>}
      </div>
      <div className="wp-list">
        {notes}
        {shown.map(size => (
          <WeldingBin
            key={size.itemId}
            size={size}
            horizonDays={horizonDays}
            flags={flagMap.get(String(size.itemId))}
            myId={myId}
            onStop={stop}
            stopping={stopping}
            onOpen={() => { setCountFirst(false); setOpenItemId(size.itemId); }}
            onCount={() => { setCountFirst(true); setOpenItemId(size.itemId); }}
            countReason={binCountReason}
          />
        ))}
      </div>
      {answerBar}
      {openSize && <WeldingSizeSheet {...sheetProps} />}
      {reservesList}
    </div>
  );
}
